import { PortalRoutes } from "constants/routes";
import history from "features/app/History";
import React, { useMemo } from "react";
import { connect } from "react-redux";
import * as actions from "redux/actions";
import type { RootState } from "redux/reducers";
import { getSelectedClientFromStorage } from "shared/storageUtils";
import type {
  NotifyFn,
  UserManagementFilter,
} from "../../userManagement.types";
import { useChangeUserStatus } from "./hooks/useChangeUserStatus";
import { useGetUsers } from "./hooks/useGetUsers";
import { useUserPasswordChange } from "./hooks/useUserPasswordChange";
import type { User } from "./userList.types";
import UserListPage from "./userListPage";
import { toUserManagementFilter } from "./userList.utils";

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;
type UserListContainerProps = StateProps & DispatchProps;

const UserListContainer = ({
  filter,
  notifyError,
  notifySuccess,
  setFilter,
  toggleLoading,
}: UserListContainerProps) => {
  const clientId = getSelectedClientFromStorage();
  const userManagementFilter = useMemo(
    () => toUserManagementFilter(filter),
    [filter]
  );

  const handleNotify: NotifyFn = (message, variant) => {
    if (variant === "success") {
      notifySuccess({ mensagem: message });
      return;
    }

    notifyError({ mensagem: message });
  };

  const {
    handleToggleUserStatus,
    lastUpdate,
    renderInactivateUserDialog: InactiveUserDialog,
  } = useChangeUserStatus({ clientId, onNotify: handleNotify, toggleLoading });

  const { getUsers } = useGetUsers({
    clientId,
    filter: userManagementFilter,
    onNotify: handleNotify,
  });

  const { handleCopyPasswordLink, handleSendPasswordLink } =
    useUserPasswordChange({
      onNotify: handleNotify,
      toggleLoading,
    });

  const saveFilterInRedux = (userManagementFilter: UserManagementFilter) => {
    setFilter({ ...filter, userManagementFilter });
  };

  const onFilterChange = (filter: UserManagementFilter) => {
    saveFilterInRedux(filter);
  };

  const handleNewUser = () => {
    history.push(PortalRoutes.CREATE_NEW_USER);
  };

  const handleEditUser = (user: User) => {
    const route = PortalRoutes.EDIT_USER.replace(
      ":idUsuario",
      user.id.toString()
    );
    history.push(route);
  };

  return (
    <>
      <UserListPage
        clientId={clientId}
        filter={userManagementFilter}
        getUsers={getUsers}
        lastUpdate={lastUpdate}
        onChangeStatus={handleToggleUserStatus}
        onCopyLink={handleCopyPasswordLink}
        onEdit={handleEditUser}
        onFilterChange={onFilterChange}
        onNewUser={handleNewUser}
        onSendLink={handleSendPasswordLink}
      />
      <InactiveUserDialog />
    </>
  );
};

const mapStateToProps = ({ filter }: RootState) => ({
  filter,
});

export const mapDispatchToProps = {
  notifyError: actions.notifyError,
  notifySuccess: actions.notifySuccess,
  setFilter: actions.setFilter,
  toggleLoading: actions.exibirCarregando,
};

export default connect<StateProps, DispatchProps, unknown, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(UserListContainer);
